// A small FIFO concurrency limiter.
//
// `createLimiter(max)` returns `run(task)`: the task starts as soon as fewer
// than `max` tasks are in flight, otherwise it waits its turn. `run` resolves
// or rejects with whatever the task does, and the slot is freed when the task
// settles, whichever way.
//
// Used to keep the comms press-kit pages from bursting: the Supabase Storage
// API needs a database connection for every public object read and every
// upload, and the project's pooler is small enough that a few hundred
// simultaneous thumbnail requests are turned away with HTTP 429. Spacing the
// work out costs a little wall-clock time and gets every image on screen.
export function createLimiter(max) {
  if (!(max >= 1)) throw new Error('createLimiter: max must be at least 1');
  let active = 0;
  const queue = [];

  const start = ({ task, resolve, reject }) => {
    active += 1;
    let result;
    try {
      result = Promise.resolve(task());
    } catch (e) {
      result = Promise.reject(e);
    }
    result.then(resolve, reject).finally(() => {
      active -= 1;
      drain();
    });
  };

  const drain = () => {
    while (active < max && queue.length) start(queue.shift());
  };

  const run = task => new Promise((resolve, reject) => {
    queue.push({ task, resolve, reject });
    drain();
  });
  // Exposed for tests and diagnostics.
  run.active = () => active;
  run.pending = () => queue.length;
  return run;
}

// Runs the task straight away, on the same tick, with the same promise shape
// as a limiter. Stands in for a limiter where throttling is not wanted.
export function unlimited(task) {
  try {
    return Promise.resolve(task());
  } catch (e) {
    return Promise.reject(e);
  }
}
